import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, Subject, switchMap, tap } from 'rxjs';

import { AgentSource } from 'AgentApp/ts/interfaces/client-context';

import { AgentSuggestedPromptsService } from 'AgentApp/ts/services/agent-suggested-prompts.service';

import { IconModule } from 'Angular/features/directives/icon/icon.module';

const SKELETON_ROWS = [0, 1, 2];

@Component({
    selector: 'ycn-agent-suggested-prompts',
    imports: [IconModule],
    templateUrl: './agent-suggested-prompts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSuggestedPromptsComponent implements OnInit {
    private readonly _suggestedPromptsService = inject(AgentSuggestedPromptsService);

    readonly source = input.required<AgentSource>();

    readonly promptSelected = output<string>();

    protected readonly prompts = signal<string[]>([]);

    protected readonly isLoading = signal(false);

    protected readonly skeletonRows = SKELETON_ROWS;

    private readonly _loadRequests = new Subject<boolean>();

    constructor() {
        this._loadRequests
            .pipe(
                tap(() => this.isLoading.set(true)),
                switchMap((regenerate) =>
                    this._suggestedPromptsService
                        .getSuggestedPrompts(
                            this._pageUrl(),
                            this._pageTitle(),
                            this.source(),
                            regenerate,
                            this.prompts()
                        )
                        .pipe(
                            map((result) => result.prompts ?? []),
                            catchError(() => of(null))
                        )
                ),
                takeUntilDestroyed()
            )
            .subscribe((prompts) => {
                if (prompts) {
                    this.prompts.set(prompts);
                }
                this.isLoading.set(false);
            });
    }

    ngOnInit(): void {
        this._loadRequests.next(false);
    }

    refresh(): void {
        window.pendoTrack('Refresh Suggested Prompts', { source: this.source() });
        this._loadRequests.next(true);
    }

    selectPrompt(prompt: string): void {
        window.pendoTrack('Clicked Suggested Prompt', { prompt, source: this.source() });
        this.promptSelected.emit(prompt);
    }

    private _pageUrl(): string {
        return this.source() === 'side_panel' ? window.location.href : '';
    }

    private _pageTitle(): string {
        return this.source() === 'side_panel' ? document.title : '';
    }
}
